"use client";
import { ProjectsImageSwitcherContext } from "@/app/components/seasons/ProjectsSwitcherHero";
import { cn, randomize, sleep } from "@/lib/utils";
import { useContext, useEffect, useState } from "react";
import Image from "./Image";

function ProjectsImageSwitcher({ images, duration = 4000, animationDuration = 3000, className }: { images: string[], duration?: number, animationDuration?: number, className?: string }) {
    //cached order of images by original index
    const [order, setOrder] = useState<number[]>([])
    //randomize order on image change
    useEffect(() => {
        setOrder(randomize(images.map((_, index) => index)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [loadedImages, setLoadedImages] = useState([0])
    const [cfg, setCfg] = useState<{ current: number, next: number | undefined, isAnimating: boolean }>({
        current: 0,
        next: undefined,
        isAnimating: false,
    });

    //initial load & everytime a already loaded images gets cycled again
    useEffect(() => {
        const tOut = setTimeout(() => { //timeout
            const nextImg = GetNextIndex(order, cfg.current); //get next image in order
            setCfg({
                ...cfg,
                next: nextImg
            });

            //if images was already loaded once - trigger manually as "onLoad" will not trigger
            if (loadedImages.includes(nextImg)) {
                SwapLogic()
            }

        }, duration)
        return () => clearTimeout(tOut)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfg.current, cfg.next, duration, images, order])

    async function SwapLogic() {
        if (cfg.next == undefined)
            return

        //add image to cache (loaded images does not trigger onload events)
        if (!loadedImages.includes(cfg.next))
            setLoadedImages(v => { return [...v, cfg.next || 0] })

        setCfg(cfg => {
            return { //start animation
                ...cfg,
                isAnimating: true,
            }
        });
        //wait for animation to end
        await sleep(animationDuration / 1000 - 0.3);
        //update main image first
        setCfg(cfg => {
            return {
                ...cfg,
                current: cfg.next || 0,
            }
        });
        await sleep(0.1);
        //after image update - hide top image and reset config
        setCfg(cfg => {
            return {
                ...cfg,
                next: undefined,
                isAnimating: false
            }
        });

    }

    const controller = useContext(ProjectsImageSwitcherContext);

    //trigger onChange when current image changes
    useEffect(() => {
        controller.onChange(cfg.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfg.current, controller])


    //register function for next logic
    useEffect(() => {
        if (cfg.isAnimating)
            return;
        setCfg(cfg => {
            return {
                ...cfg,
                next: GetNextIndex(order, cfg.current)
            }
        })
        SwapLogic()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controller.next])

    //register function for back logic
    useEffect(() => {
        if (cfg.isAnimating)
            return;
        setCfg(cfg => {
            return {
                ...cfg,
                next: GetPrevIndex(order, cfg.current)
            }
        })
        SwapLogic()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controller.prev])


    return (<article className={ cn(["h-full w-full relative z-10", className]) }>
        <Image quality={ 75 }
            className={ cn(["bg-inherit object-cover", !cfg.isAnimating && "z-10"]) }
            src={ "/api/uploads/" + images[cfg.current] }
            priority fill alt="" />

        { cfg.next != undefined &&
            <Image quality={ 75 }
                className={ cn(["bg-inherit object-cover", cfg.isAnimating && `animate-in fade-in z-10` || "invisible"]) }
                src={ "/api/uploads/" + images[cfg.next] }
                onLoad={ () => SwapLogic() }
                style={ { animationDuration: animationDuration + "ms" } }
                fill alt="" /> }
    </article>
    )
}
export default ProjectsImageSwitcher

/**
 * gets next index in array
 * @param array 
 * @param current 
 * @returns 
 */
export function GetNextIndex<T>(array: T[], current: T) {
    const currentIndex = array.indexOf(current);
    return array[(currentIndex + 1) % array.length]; //get next while auto cycling back to 0
}

/**
 * gets previous index in array
 * @param array 
 * @param current 
 * @returns 
 */
export function GetPrevIndex<T>(array: T[], current: T) {
    const currentIndex = array.indexOf(current);
    return array[(currentIndex - 1 + array.length) % array.length]; //get back while auto cycling back to length-1
}

